import { Bell } from 'lucide-react';
import { ChecklistGitter } from './ChecklistGitter';
import type { DetailChecklistEintrag } from '@/types/detail';

type Props = {
  /** data-tour-Name fuer die Einfuehrungstour. */
  anker?: string;
  bestellnr: string;
  bestellungId: number;
  eintraege: DetailChecklistEintrag[];
  /**
   * Wünscht die Kundin eine Nachricht, sobald ALLES da ist? (A56, #135)
   */
  nachrichtWunsch?: boolean;
  /** Die Titel, zu denen einzeln eine Nachricht gewünscht ist. */
  nachrichtTitel?: string[];
  onToggle: (feld: string, wert: boolean) => void;
};

/** Der Punkt, der den Zusatz trägt. */
const PUNKT_BENACHRICHTIGT = 'kunde_benachrichtigt';

/**
 * Der Zusatz zum Punkt „Kunde benachrichtigt" (A56.5, #135).
 *
 * WARUM DER PUNKT NICHT VERSTECKT WIRD
 *   Der Betreiber hatte vorgeschlagen, ihn nur bei vermerktem Wunsch zu
 *   zeigen. Dagegen spricht: Wer „ich habe angerufen" festhalten will,
 *   obwohl kein Wunsch vermerkt war, würde ihn dann suchen — und gerade
 *   dann ist der Anruf etwas wert. Also bleibt er IMMER da und bekommt
 *   nur einen Zusatz, sobald ein Wunsch vermerkt ist. Das Häkchen trägt
 *   den Grund mit, statt zu verschwinden.
 *
 * Exportiert, damit die Formulierung eine Zusicherung bekommt, ohne den
 * ganzen Abschnitt zu rendern.
 */
export function benachrichtigungZusatz(
  nachrichtWunsch: boolean,
  titel: readonly string[]
): string | null {
  if (nachrichtWunsch && titel.length > 0) {
    return `Kundin wünscht Nachricht, sobald alles da ist — und einzeln zu ${titel.join(', ')}`;
  }
  if (nachrichtWunsch) return 'Kundin wünscht Nachricht, sobald alles da ist';
  if (titel.length === 1) return `Kundin wünscht Nachricht, sobald ${titel[0]} da ist`;
  if (titel.length > 1) return `Kundin wünscht Nachricht zu ${titel.join(', ')}`;
  return null;
}

export function ChecklistSektion({
  anker,
  eintraege,
  nachrichtWunsch = false,
  nachrichtTitel = [],
  onToggle
}: Props) {
  if (eintraege.length === 0) return null;

  const zusatz = benachrichtigungZusatz(nachrichtWunsch, nachrichtTitel);

  return (
    <section data-tour={anker}>
      <h3 className="mb-2 font-serif text-base text-ink">Bearbeitungsstand</h3>

      {/* A56.5: Der Zusatz steht ÜBER der Reihe und nicht im Knopf —
          die Kacheln sind schmal, ein Satz darin würde umbrechen und
          jede Zeile der Reihe mit anheben. Hier steht er einmal, in
          voller Breite, und nennt den Titel.

          A58: Er steht jetzt ÜBER dem Gitter statt zwischen dessen
          beiden Hälften. Das ist die einzige sichtbare Folge der
          Zusammenlegung — vorher lag er zwischen den berechneten und
          den manuellen Kacheln, was zufällig so gewachsen war: Er
          gehört zu „Kunde benachrichtigt", und der steht unten. Über
          allem gilt er für den ganzen Abschnitt, und das ist er auch. */}
      {zusatz !== null && (
        <p className="mb-2 flex items-start gap-1.5 text-sm text-akzent-stark">
          <Bell aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{zusatz}</span>
        </p>
      )}

      <ChecklistGitter
        eintraege={eintraege}
        hinweis="Diese drei ergeben sich automatisch aus Zustand, Positionen und Sendungen."
        onToggle={onToggle}
        zusatzSymbol={(code) =>
          code === PUNKT_BENACHRICHTIGT && zusatz !== null ? (
            <Bell aria-hidden className="ml-auto h-3.5 w-3.5 shrink-0 text-akzent" />
          ) : null
        }
      />
    </section>
  );
}
